<template>
    <common-content-spacer height="medium">
        <div class="calendar__header--toggle">
            <select-input
                v-if="showMonthDaySwitch"
                class="calendar__header--toggle--select"
                :options="calendarTypeOptions"
                :prefillVal="selectedCalendarType"
                @change="toggleMonthDayView($event)"
            ></select-input>
            <common-user-selector
                v-if="selectedUserId"
                :users="availableUsers"
                :selectedUserId="selectedUserId"
                @emitEvent="emitEvent($event)"
            ></common-user-selector>
        </div>
        <div v-if="showToggle" class="calendar__header">
            <div class="calendar__left-side">
                <slot name="left-side"></slot>
            </div>
            <div class="calendar__toggle--wrapper">
                <div v-if="showTitleInfo" class="calendar__toggle--title">
                    <common-button
                        v-if="categoriesLength > 1 && !calendarEventId"
                        type="secondary"
                        size="micro"
                        :label="$t('common.microcopy.back')"
                        @click="emitEvent({ name: 'unselect-category' })"
                    ></common-button>
                    <div class="calendar__toggle--tooltip">
                        <p v-if="getCategoriesTitle?.length">
                            <common-info-tip>
                                <p v-for="(item, index) in getCategoriesTitle" :key="index">{{ item }}</p>
                            </common-info-tip>
                        </p>
                        <p>{{ getTranslation(offer?.titleTextContent) }}</p>
                    </div>
                    <common-social-share
                        v-if="showShare"
                        class="calendar__toggle--socialshare"
                        :title="getTranslation(offer?.titleTextContent)"
                        :description="getTranslation(offer?.descriptionTextContent)"
                    ></common-social-share>
                </div>
                <div v-if="!regosIframe" class="calendar__toggle--container">
                    <div class="calendar__toggle">
                        <div class="calendar__reply-solid calendar__reply-solid-left">
                            <common-icon
                                v-if="showBackArrow && showBlinkingArrows"
                                name="reply-solid"
                                size="small"
                                class="calendar-solid__icon-left"
                            ></common-icon>
                        </div>
                        <div>
                            <button
                                :class="arrowButtonClass"
                                :disabled="!showBackArrow"
                                type="button"
                                @click="showMonth ? goMonthBack() : goDayBack()"
                            >
                                <common-icon name="arrow-back" size="small"></common-icon>
                            </button>
                        </div>
                        <div class="calendar__month-label">
                            <p>{{ dateLabel }}</p>
                            <p v-if="!showMonth">{{ dayjs(currentSelectedDate.date).format('DD') }}</p>
                        </div>

                        <div>
                            <button class="calendar__button" type="button" @click="showMonth ? goMonthForward() : goDayForward()">
                                <common-icon name="arrow-forward" size="small"></common-icon>
                            </button>
                        </div>
                        <div class="calendar__reply-solid calendar__reply-solid-right">
                            <common-icon
                                v-if="showBlinkingArrows"
                                name="reply-solid"
                                size="small"
                                class="calendar__icon-right"
                            ></common-icon>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="!regosIframe" class="calendar__free-schedule">
            <checkbox-input
                v-if="showFreeSchedule"
                class="calendar__free-schedule--flexible"
                :secondLabel="$t('common.microcopy.flexible')"
                type="switch"
                labelPosition="left"
                :value="freeSchedule"
                @change="changeFreeSchedule"
            ></checkbox-input>
            <common-button
                v-if="showFreeSchedule"
                type="link"
                size="micro"
                :label="$t('common.microcopy.free_reservation')"
                @click="openCustomReservatinModal()"
            ></common-button>
        </div>
        <div v-if="regosIframe?.length">
            <iframe
                ref="regosIframe"
                style="overflow-y: hidden"
                width="100%"
                height="400px"
                :src="regosIframe"
                frameborder="0"
            ></iframe>
        </div>
        <div v-else class="calendar-week-x-wrapper">
            <section v-if="showMonth" class="calendar">
                <common-soldout v-if="showSoldOut" :label="labelForSoldOut"></common-soldout>
                <header class="calendar__weekdays">
                    <h5 v-for="(weekday, index) in weekDays" :key="index" class="calendar__weekday">
                        {{ weekday }}
                    </h5>
                </header>

                <ol class="calendar__days">
                    <li v-for="(day, index) in monthDays" :key="index" :class="calendarDay(day)" @click="openDayModal(day)">
                        <div v-if="isToday(day)" class="calendar__day--today"></div>
                        <span :class="calendarDate(day)">
                            {{ day }}
                            <span v-if="showTodayLabel(day)">{{ $t('common.calendar.today').toUpperCase() }}</span>
                        </span>

                        <div v-if="topEventsCount(day)" class="calendar__top-events-count">
                            <span v-if="!isTabletSmall">{{ $t(topEvents.titlePath, { n: topEventsCount(day) }) }}</span>
                            <span v-else>{{ topEventsCount(day) }}</span>
                        </div>

                        <div v-if="bottomEventsCount(day)" class="calendar__bottom-events-count">
                            <span v-if="!isTabletSmall">
                                &nbsp;{{ $t(bottomEvents.titlePath, { n: bottomEventsCount(day) }) }}
                            </span>
                            <span v-else>{{ bottomEventsCount(day) }}</span>
                        </div>

                        <div v-if="!day && index > 27" class="calendar__empty-events" @click.stop="goMonthForward()">
                            <common-icon
                                name="right-solid"
                                :size="isTabletSmall ? 'medium' : 'large'"
                                class="calendar-solid__icon-left"
                            ></common-icon>
                            <p>{{ $t('pages.calendar.my_calendar.go_to_next_month') }}</p>
                        </div>
                    </li>
                </ol>
            </section>
            <div v-else>
                <common-calendar-x
                    ref="calendarX"
                    :currentDate="currentSelectedDate"
                    :userId="selectedUserId"
                    :excludedOfferIds="excludedOfferIds"
                    @calendar-event-clicked="onCalendarEventClicked"
                ></common-calendar-x>
            </div>
        </div>
        <calendar-single-event-modal
            v-if="isSingleEventModalOpen"
            :calendarEvent="selectedCalendarEvent"
            @close="closeSingleEventModal"
        ></calendar-single-event-modal>
    </common-content-spacer>
</template>

<script>
    import CalendarMixin from 'mixins/CalendarMixin'
    import WindowResizeMixin from 'mixins/WindowResizeMixin'
    import TranslateMixin from 'mixins/TranslateMixin'
    import CheckboxInput from 'components/form-inputs/CheckboxInput'
    import SelectInput from 'components/form-inputs/SelectInput'
    import RoutesEnum from 'enums/routes.enum'
    import CalendarTypeEnum from 'enums/calendar_type.enum'
    import dayjs from 'dayjs'
    import CalendarSingleEventModal from 'components/modals/calendar/CalendarSingleEventModal'

    export default {
        mixins: [CalendarMixin, WindowResizeMixin, TranslateMixin],

        components: {
            CheckboxInput,
            SelectInput,
            CalendarSingleEventModal,
        },

        props: {
            showToggle: { type: Boolean, default: true },
            topEvents: { type: Object, default: null },
            bottomEvents: { type: Object, default: null },
            showSoldOut: { type: Boolean, default: false },
            showBlinkingArrows: { type: Boolean, default: false },
            labelForSoldOut: { type: String, default: '' },
            innerWidth: { type: Number, default: 0 },
            innerHeight: { type: Number, default: 0 },
            allowGoingBackward: { type: Boolean, default: false },
            categories: { type: Array, default: undefined },
            categoriesLength: { type: Number, default: 0 },
            calendarEventId: { type: [Number, String], default: undefined },
            offer: { type: Object, default: undefined },
            showShare: { type: Boolean, default: true },
            showFreeSchedule: { type: Boolean, default: false },
            availableUsers: { type: Array, default: new Array() },
            selectedUser: { type: Number, default: undefined },
            selectedUserId: { type: Number, default: undefined },
            selectedDate: { type: String, default: undefined },
            showMonthDaySwitch: { type: Boolean, default: false },
            regosIframe: { type: String, default: undefined },
            excludedOfferIds: { type: Array, default: () => [] },
        },

        data: () => ({
            // this must be an object because of reactivity
            currentSelectedDate: { date: undefined },
            selectedDay: null,
            currentMonth: null,
            currentYear: null,
            freeSchedule: false,
            showMonth: true,
            dayjs: dayjs,
            isCustomReservationModalOpen: false,
            selectedCalendarType: CalendarTypeEnum.MONTLY,
            isSingleEventModalOpen: false,
            selectedCalendarEvent: undefined,
        }),

        computed: {
            showTitleInfo() {
                return this.getCategoriesTitle?.length || (this.categoriesLength > 1 && !this.calendarEventId)
            },
            calendarTypeOptions() {
                return Object.entries(CalendarTypeEnum).map(([key, value]) => ({
                    label: this.$t(`common.microcopy.${key.toLowerCase()}`),
                    value: value,
                }))
            },

            arrowButtonClass() {
                return {
                    calendar__button: true,
                    'calendar__button-disabled': !this.showBackArrow,
                }
            },
            showBackArrow() {
                return (
                    this.allowGoingBackward || (this.currentMonth !== this.month || this.currentYear !== this.year ? true : false)
                )
            },

            getCategoriesTitle() {
                return this.categories?.map((item) => {
                    if (this.getTranslation(item?.titleTextContent).length > 20) {
                        return `${this.getTranslation(item?.titleTextContent).substring(0, 20)}...`
                    } else {
                        return this.getTranslation(item?.titleTextContent)
                    }
                })
            },
        },

        methods: {
            calendarDay(day) {
                return {
                    calendar__day: true,
                    'calendar__day--today-text': this.isToday(day),
                    'calendar__day--has-slots': !!this.topEventsCount(day) || !!this.bottomEventsCount(day),
                    'calendar__day--selected': day === this.selectedDay && day !== null,
                }
            },

            calendarDate(day) {
                return {
                    calendar__date: true,
                    'calendar__date--no-slots': !this.topEventsCount(day) && !this.bottomEventsCount(day),
                }
            },

            topEventsCount(day) {
                return this.topEvents?.dates?.find((slot) => slot.date === this.getDate(day))?.count
            },

            bottomEventsCount(day) {
                return this.bottomEvents?.dates?.find((slot) => slot.date === this.getDate(day))?.count
            },

            openDayModal(day) {
                this.emitEvent({
                    name: 'modal-date-change',
                    payload: { date: this.getDate(day) },
                })
            },

            toggleMonthDayView(type, redirect = true) {
                this.selectedCalendarType = type.value
                if (type.value === CalendarTypeEnum.MONTLY) {
                    this.showMonth = true
                } else {
                    this.showMonth = false
                    const calendarEl = document.querySelector('.calendar-week-x-wrapper')
                    if (type.value === CalendarTypeEnum.WEEKLY) {
                        calendarEl.classList.add('calendar-week-x')
                        setTimeout(() => {
                            this.$refs.calendarX?.calendarControls.setView('week')
                        }, 200)
                    } else {
                        calendarEl.classList.remove('calendar-week-x')
                        this.$refs.calendarX?.calendarControls.setView('day')
                    }
                }

                if (!this.showMonth) {
                    this.closeModal()
                    const date = dayjs(this.currentSelectedDate.date)
                    this.setCurrentDate(date)
                    if (redirect) {
                        this.$router.push({
                            name: RoutesEnum.CALENDAR.MY,
                            params: { type: CalendarTypeEnum.DAILY },
                            query: { date: date.format('YYYY-MM-DD') },
                        })
                    }
                } else {
                    if (redirect) {
                        this.$router.push({ name: RoutesEnum.CALENDAR.MY, params: { type: CalendarTypeEnum.MONTLY } })
                    }
                    this.loadCurrentMonth()
                }
            },

            closeModal() {
                this.emitEvent({ name: 'modal-date-change', payload: { date: null } })
            },

            showTodayLabel(day) {
                if (this.isTabletSmall) {
                    return false
                }
                return this.isToday(day)
            },

            changeFreeSchedule() {
                this.freeSchedule = !this.freeSchedule
                this.emitEvent({ name: 'free-schedule-change', payload: this.freeSchedule })
            },

            goDayBack() {
                let date = dayjs(this.currentSelectedDate.date).subtract(1, 'day')
                if (this.$refs.calendarX.calendarControls.getView() === 'week') {
                    date = dayjs(this.currentSelectedDate.date).subtract(7, 'day')
                }
                this.setCurrentDate(date)
                this.$router.push({ path: this.$route.path, query: { date: date.format('YYYY-MM-DD') } })
            },

            goDayForward() {
                let date = dayjs(this.currentSelectedDate.date).add(1, 'day')
                if (this.$refs.calendarX.calendarControls.getView() === 'week') {
                    date = dayjs(this.currentSelectedDate.date).add(7, 'day')
                }
                this.setCurrentDate(date)
                this.$router.push({ path: this.$route.path, query: { date: date.format('YYYY-MM-DD') } })
            },

            setCurrentDate(date) {
                this.currentSelectedDate.date = date.format('YYYY-MM-DD')
                this.month = date.get('month')
                this.year = date.get('year')
                setTimeout(() => {
                    this.$refs.calendarX?.calendarControls?.setDate(this.currentSelectedDate.date)
                }, 200)
            },

            openCustomReservatinModal() {
                this.emitEvent({ name: 'open-custom-reservation-modal' })
            },

            switchDayMonthView() {
                this.$nextTick(() => {
                    if (this.$refs.checkboxMonthDaySwitch) {
                        this.$refs.checkboxMonthDaySwitch.changeInputValue()
                    }
                })
            },

            onCalendarEventClicked(payload) {
                this.selectedCalendarEvent = payload
                this.isSingleEventModalOpen = true
            },

            closeSingleEventModal() {
                this.isSingleEventModalOpen = false
            },
        },

        watch: {
            selectedDate() {
                const date = dayjs(this.selectedDate)
                this.setCurrentDate(date)
            },
        },

        beforeUnmount() {
            window.removeEventListener('popstate', this.switchDayMonthView)
        },

        mounted() {
            const dateQuery = this.$route.query.date
            const d = dateQuery ? new Date(dateQuery) : new Date()
            this.currentMonth = d.getMonth()
            this.currentYear = d.getFullYear()
            this.month = d.getMonth()
            this.year = d.getFullYear()
            this.currentSelectedDate.date = d
            if (this.$route.params.type === CalendarTypeEnum.DAILY) {
                this.switchDayMonthView()
            }
            window.addEventListener('popstate', this.switchDayMonthView)
        },
    }
</script>

<style lang="scss">
    .calendar-week-x-wrapper {
        min-width: 100%;
        width: 100%;
    }

    .calendar-week-x {
        .sx__week-grid,
        .sx__week-grid__date-axis {
            min-width: 800px;
            overflow: auto;
        }
    }
</style>
